import { createRouter } from '@tanstack/react-router';

import { routeTree } from '@/routes/routeTree.gen';
import { telemetry } from '@/lib/telemetry';
import { useRefusalAnnouncementStore } from '@/lib/stores/refusal-announcement-store';
import { queryClient } from './query-client';
import { setRouter } from './router-ref';

/*
 * Router singleton (spec §4.1). Query client is threaded through router context
 * so loaders can call ensureQueryData. The router ref is registered so the
 * global 4xx handlers can navigate without a boot ordering constraint.
 */
export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
});

setRouter(router);

/*
 * Page-view telemetry (spec §13 observability). `onResolved` fires exactly once
 * per completed navigation — after loaders settle and before the pending state
 * is torn down — so this cannot double-fire on the pending phase. We record the
 * matched leaf route id (e.g. `/_auth/invoices/$invoiceId`), the ROUTE PATTERN
 * with `$param` placeholders, never the concrete URL, so no entity ids reach
 * RUM. Disabled telemetry makes this a silent no-op (module default).
 */
router.subscribe('onResolved', () => {
  const matches = router.state.matches;
  const leaf = matches[matches.length - 1];
  if (leaf) {
    telemetry.recordPageView(leaf.routeId);
  }

  /*
   * Clear a stale refusal announcement on navigation (finding 13) so the
   * generic refusal does not linger in the live region across pages. A refusal
   * on a 401/403 is announced by the interceptor and immediately redirects to
   * /login or /no-access, so those destinations are EXEMPT — clearing them
   * would wipe the announcement before it is read. Any OTHER completed
   * navigation (the user moving on) clears it.
   */
  const pathname = router.state.location.pathname;
  if (pathname !== '/no-access' && pathname !== '/login') {
    useRefusalAnnouncementStore.getState().clear();
  }
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
